import {
  AUTH_ROUTES,
  forgotPasswordResponseSchema,
  googleAuthResponseSchema,
  loginResponseSchema,
  logoutResponseSchema,
  meResponseSchema,
  registerResponseSchema,
  resendVerificationResponseSchema,
  resetPasswordResponseSchema,
  verifyEmailResponseSchema,
  type AuthSession,
  type ForgotPasswordRequest,
  type LoginRequest,
  type PublicUser,
  type RegisterRequest,
  type ResendVerificationRequest,
  type ResetPasswordRequest,
  type VerifyEmailRequest,
} from "@nh/shared";
import { apiFetch } from "@/shared/api/client";
import { readReferral } from "@/features/network/referral.storage";
import { authRouteFetch } from "@/shared/api/session";

/**
 * Llamadas de autenticación.
 *
 * `AUTH_ROUTES` ya trae el prefijo `/api`, por eso estas llamadas pasan
 * `baseUrl: ""`: si no, `apiFetch` volvería a anteponerlo.
 *
 * Registro y login usan `apiFetch` a secas — todavía no hay token que
 * adjuntar ni sesión que renovar. Lo demás va por `authRouteFetch`, que
 * sí adjunta el token y renueva ante `TOKEN_EXPIRED`.
 */
const NO_PREFIX = { baseUrl: "" } as const;

export function registerRequest(input: RegisterRequest): Promise<AuthSession> {
  return apiFetch(AUTH_ROUTES.register, registerResponseSchema, {
    ...NO_PREFIX,
    method: "POST",
    body: input,
  });
}

export function loginRequest(input: LoginRequest): Promise<AuthSession> {
  return apiFetch(AUTH_ROUTES.login, loginResponseSchema, {
    ...NO_PREFIX,
    method: "POST",
    body: input,
  });
}

/**
 * Entrar (o registrarse: el servidor decide) con el `credential` que
 * entrega el botón oficial de Google. La respuesta es la MISMA sesión que
 * el login con contraseña, y por la misma razón que él va por `apiFetch` a
 * secas: todavía no hay token que adjuntar ni sesión que renovar.
 */
export function googleAuthRequest(credential: string): Promise<AuthSession> {
  /*
   * La invitación en tránsito (`/register/CODE` → sessionStorage) viaja
   * también cuando el registro es con GOOGLE: misma red, misma regla.
   * `readReferral` es tolerante — sin invitación, no viaja nada.
   */
  const referral = readReferral();
  return apiFetch(AUTH_ROUTES.google, googleAuthResponseSchema, {
    ...NO_PREFIX,
    method: "POST",
    body: { credential, ...(referral ? { referralCode: referral.code } : {}) },
  });
}

/**
 * Pide el enlace para crear una contraseña nueva.
 *
 * Va por `apiFetch` y no por `authRouteFetch` por la misma razón que el
 * login: quien ha perdido su contraseña no tiene sesión que adjuntar ni
 * que renovar.
 *
 * Devuelve SIEMPRE `{ requested: true }`, exista o no una cuenta con ese
 * correo. No es una simplificación de este archivo: es el contrato (ver
 * `forgotPasswordResponseSchema`). El frontend no puede filtrar lo que no
 * recibe, y por eso no hay nada que decidir aquí.
 */
export function forgotPasswordRequest(
  input: ForgotPasswordRequest,
): Promise<{ requested: true }> {
  return apiFetch(AUTH_ROUTES.forgotPassword, forgotPasswordResponseSchema, {
    ...NO_PREFIX,
    method: "POST",
    body: input,
  });
}

/**
 * Escribe la contraseña nueva con el token del correo.
 *
 * El token va en el CUERPO, nunca en la URL: las URLs quedan en el
 * historial del navegador, en los logs de cualquier proxy del camino y en
 * la cabecera `Referer` que se manda al siguiente sitio. Una llave de
 * recuperación no debe pasar por ninguno de los tres.
 *
 * Y sin sesión que adjuntar: quien ha perdido su contraseña no tiene
 * ninguna. Por eso `apiFetch` y no `authRouteFetch`.
 */
export function resetPasswordRequest(
  input: ResetPasswordRequest,
): Promise<{ reset: true }> {
  return apiFetch(AUTH_ROUTES.resetPassword, resetPasswordResponseSchema, {
    ...NO_PREFIX,
    method: "POST",
    body: input,
  });
}

/**
 * Canjea el token del correo de verificación.
 *
 * Devuelve el correo que quedó verificado —para prellenar el login—, no
 * una sesión: verificar el correo no es entrar. Quien abrió el enlace
 * pudo hacerlo desde el teléfono mientras la cuenta se creó en la
 * computadora, y dar sesión ahí sería dársela al dispositivo equivocado.
 */
export function verifyEmailRequest(
  input: VerifyEmailRequest,
): Promise<{ verified: true; email: string }> {
  return apiFetch(AUTH_ROUTES.verifyEmail, verifyEmailResponseSchema, {
    ...NO_PREFIX,
    method: "POST",
    body: input,
  });
}

/**
 * Pide otro enlace de verificación.
 *
 * Devuelve SIEMPRE `{ requested: true }`, exista o no la cuenta y esté o
 * no verificada. Es el contrato (ver `resendVerificationResponseSchema`) y
 * por la misma razón que en la recuperación de contraseña: distinguir los
 * casos convertiría esta ruta en un buscador de quién tiene cuenta.
 */
export function resendVerificationRequest(
  input: ResendVerificationRequest,
): Promise<{ requested: true }> {
  return apiFetch(AUTH_ROUTES.resendVerification, resendVerificationResponseSchema, {
    ...NO_PREFIX,
    method: "POST",
    body: input,
  });
}

export function logoutRequest(): Promise<{ loggedOut: true }> {
  return authRouteFetch(AUTH_ROUTES.logout, logoutResponseSchema, { method: "POST" });
}

export function meRequest(): Promise<{ user: PublicUser }> {
  return authRouteFetch(AUTH_ROUTES.me, meResponseSchema);
}

/** URL a la que se manda al navegador para arrancar el flujo de Google. */
export const GOOGLE_START_URL = AUTH_ROUTES.googleStart;
